/**
 * Phase 2.2: Progress Tiles
 * 
 * Reusable tile components for displaying progress metrics.
 * Simple, clean, positive, non-technical.
 */

import React from 'react';

// ==================== BASE TILE COMPONENT ====================

const BaseTile = ({ 
  icon, 
  label, 
  value, 
  subtext, 
  trend, // 'up' | 'down' | 'neutral' | null
  trendIsGood, // true if this trend direction is positive
  color = 'blue',
  className = '' 
}) => {
  const colorStyles = {
    blue: 'bg-blue-50 border-blue-100',
    green: 'bg-green-50 border-green-100',
    yellow: 'bg-yellow-50 border-yellow-100',
    purple: 'bg-purple-50 border-purple-100',
    pink: 'bg-pink-50 border-pink-100',
    gray: 'bg-gray-50 border-gray-100'
  };

  const getTrendIcon = () => {
    if (!trend || trend === 'neutral') return null;
    
    const isPositive = trendIsGood;
    const trendColor = isPositive ? 'text-green-500' : 'text-amber-500';
    
    if (trend === 'up') {
      return <span className={trendColor}>↑</span>;
    } else if (trend === 'down') {
      return <span className={trendColor}>↓</span>;
    }
    return null;
  };

  return (
    <div className={`p-4 rounded-xl border ${colorStyles[color]} ${className}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{icon}</span>
        <span className="text-sm text-gray-500 font-medium">{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-gray-800">{value}</span>
        {getTrendIcon()}
      </div>
      {subtext && (
        <p className="text-sm text-gray-500 mt-1">{subtext}</p>
      )}
    </div>
  );
};

// ==================== WEIGHT CHANGE TILE ====================

export const WeightChangeTile = ({ 
  currentWeight, 
  previousWeight, 
  unit = 'kg',
  className = '' 
}) => {
  const change = currentWeight && previousWeight 
    ? (currentWeight - previousWeight).toFixed(1) 
    : null;
  
  const changeNum = parseFloat(change);
  const trend = changeNum > 0 ? 'up' : changeNum < 0 ? 'down' : 'neutral';
  
  // For weight loss goals, down is good
  const displayChange = change 
    ? `${changeNum > 0 ? '+' : ''}${change} ${unit}`
    : '—';

  const subtext = !change 
    ? 'Not enough data yet'
    : changeNum === 0 
      ? 'Holding steady'
      : changeNum < 0 
        ? 'Moving in the right direction!'
        : 'A little fluctuation is normal';

  return (
    <BaseTile
      icon="⚖️"
      label="This Week"
      value={displayChange}
      subtext={subtext}
      trend={trend}
      trendIsGood={changeNum <= 0}
      color={changeNum <= 0 ? 'green' : 'yellow'}
      className={className}
    />
  );
};

// ==================== TOTAL PROGRESS TILE ====================

export const TotalProgressTile = ({ 
  startingWeight, 
  currentWeight, 
  goalWeight,
  unit = 'kg',
  className = '' 
}) => {
  const totalLost = startingWeight && currentWeight 
    ? (startingWeight - currentWeight).toFixed(1) 
    : null;
  
  const totalLostNum = parseFloat(totalLost);
  
  // Calculate percentage to goal
  const totalToLose = startingWeight && goalWeight 
    ? startingWeight - goalWeight 
    : null;
  const percentComplete = totalToLose && totalLostNum 
    ? Math.min(100, Math.round((totalLostNum / totalToLose) * 100))
    : null;

  const displayValue = totalLost 
    ? `${totalLostNum > 0 ? '-' : '+'}${Math.abs(totalLostNum)} ${unit}`
    : '—';

  const subtext = !totalLost 
    ? 'Your journey is just beginning'
    : percentComplete !== null
      ? `${percentComplete}% of the way to your goal!`
      : totalLostNum > 0 
        ? 'Great progress so far!'
        : 'Every journey has its pace';

  return (
    <BaseTile
      icon="🎯"
      label="Total Progress"
      value={displayValue}
      subtext={subtext}
      trend={totalLostNum > 0 ? 'down' : 'neutral'}
      trendIsGood={totalLostNum > 0}
      color="purple"
      className={className}
    />
  );
};

// ==================== CONSISTENCY TILE ====================
// Score is 1-10 where 10 = very consistent (good), 1 = inconsistent (bad)

export const ConsistencyTile = ({ 
  score, // 1-10 scale (10 = best)
  className = '' 
}) => {
  const percentage = score ? Math.round((score / 10) * 100) : null;
  
  const getLabel = () => {
    if (!score) return 'Not recorded';
    if (score >= 8) return 'Crushing it! 💪';
    if (score >= 6) return 'Solid effort!';
    if (score >= 4) return 'Room to grow';
    return "Tough week, that's okay";
  };

  const getColor = () => {
    if (!score) return 'gray';
    if (score >= 8) return 'green';
    if (score >= 6) return 'blue';
    return 'yellow';
  };

  const barColorClass = score >= 8 ? 'bg-green-400' : 
    score >= 6 ? 'bg-blue-400' : 
    score >= 4 ? 'bg-yellow-400' : 'bg-gray-300';

  return (
    <div className={`p-4 rounded-xl border bg-${getColor()}-50 border-${getColor()}-100 ${className}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">📊</span>
        <span className="text-sm text-gray-500 font-medium">Consistency</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-gray-800">
          {percentage !== null ? `${percentage}%` : '—'}
        </span>
      </div>
      {/* Progress Bar */}
      <div className="w-full h-2 bg-gray-200 rounded-full mt-2 overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all duration-500 ${barColorClass}`}
          style={{ width: `${percentage || 0}%` }}
        />
      </div>
      <p className="text-sm text-gray-500 mt-2">{getLabel()}</p>
    </div>
  );
};

// ==================== ADJUSTMENT TILE ====================

export const AdjustmentTile = ({ 
  hasAdjustment,
  action, // 'maintain' | 'cut' | 'increase'
  calorieChange, // number (positive or negative)
  bucketMessage, // from bucketMessages.js
  status, // 'approved' | 'pending_review' | 'none'
  className = '' 
}) => {
  const getDisplayInfo = () => {
    // Calorie cut (reduction)
    if (calorieChange < 0) {
      return {
        icon: '📉',
        title: 'Calories Reduced',
        statusLabel: 'Calories Reduced',
        subtext: bucketMessage || 'A small adjustment to keep progress moving.',
        color: 'blue'
      };
    }
    
    // Calorie increase
    if (calorieChange > 0) {
      return {
        icon: '📈',
        title: 'Calories Increased',
        statusLabel: 'Calories Increased',
        subtext: bucketMessage || 'A little more fuel to keep you feeling good.',
        color: 'purple'
      };
    }
    
    // No change (maintain)
    return {
      icon: '✨',
      title: 'No Changes',
      statusLabel: 'No Changes',
      subtext: bucketMessage || "Your plan stays the same, you're on track!",
      color: 'green'
    };
  };

  const info = getDisplayInfo();

  return (
    <div className={`p-4 rounded-xl border bg-${info.color}-50 border-${info.color}-100 ${className}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{info.icon}</span>
        <span className="text-sm text-gray-500 font-medium">This Week&apos;s Plan</span>
      </div>
      <div className="text-lg font-semibold text-gray-800 mb-1">
        {info.statusLabel}
      </div>
      <p className="text-sm text-gray-500">{info.subtext}</p>
    </div>
  );
};

// ==================== WEEKEND CONSISTENCY TILE ====================

export const WeekendTile = ({ 
  weekendScore, // 0-100 percentage
  weekdayImplied, // calculated from overall and weekend
  className = '' 
}) => {
  const getLabel = () => {
    if (weekendScore === null || weekendScore === undefined) return 'Not recorded';
    if (weekendScore >= 80) return 'Weekend warrior! 🎉';
    if (weekendScore >= 60) return 'Pretty solid weekend';
    if (weekendScore >= 40) return 'Weekends are tricky';
    return 'Room to improve';
  };

  const getColor = () => {
    if (weekendScore === null || weekendScore === undefined) return 'gray';
    if (weekendScore >= 80) return 'green';
    if (weekendScore >= 60) return 'blue';
    return 'yellow';
  };

  return (
    <BaseTile
      icon="🗓️"
      label="Weekend Consistency"
      value={weekendScore !== null ? `${weekendScore}%` : '—'}
      subtext={getLabel()}
      color={getColor()}
      className={className}
    />
  );
};

// ==================== HUNGER/ENERGY/STRESS MINI TILES ====================
// All scores are 1-10 where 10 = GOOD, 1 = BAD
// - Hunger: 10 = satisfied, 1 = very hungry
// - Energy: 10 = energized, 1 = exhausted
// - Stress: 10 = calm/stress-free, 1 = very stressed

export const ComfortTile = ({ 
  type, // 'hunger' | 'energy' | 'stress'
  score, // 1-5 scale from check-in (5 = best, 1 = worst)
  className = '' 
}) => {
  const configs = {
    hunger: {
      icon: '🍽️',
      label: 'Hunger',
      maxScore: 5,
      getText: (s) => {
        if (s >= 4) return 'Feeling satisfied';
        if (s === 3) return 'Moderate hunger';
        if (s === 2) return 'Pretty hungry';
        return 'Very hungry';
      }
    },
    energy: {
      icon: '⚡',
      label: 'Energy',
      maxScore: 5,
      getText: (s) => {
        if (s >= 4) return 'Feeling energized!';
        if (s === 3) return 'Decent energy';
        if (s === 2) return 'Energy is low';
        return 'Exhausted';
      }
    },
    stress: {
      icon: '🧘',
      label: 'Stress',
      maxScore: 5,
      getText: (s) => {
        if (s >= 4) return 'Feeling calm';
        if (s === 3) return 'Some stress';
        if (s === 2) return 'Stressed';
        return 'Very stressed';
      }
    }
  };

  const config = configs[type];
  if (!config) return null;

  // 5 = best (green), 1-2 = bad (amber/red)
  const isGood = score >= 4;

  return (
    <div className={`p-3 rounded-lg border bg-gray-50 border-gray-100 ${className}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <span>{config.icon}</span>
          <span className="text-xs text-gray-500">{config.label}</span>
        </div>
        <span className={`text-sm font-medium ${isGood ? 'text-green-600' : 'text-amber-600'}`}>
          {score || '—'}/5
        </span>
      </div>
      <p className="text-xs text-gray-400">{score ? config.getText(score) : 'Not recorded'}</p>
    </div>
  );
};

// ==================== PROGRESS TILES GRID ====================

export const ProgressTilesGrid = ({
  currentWeight,
  previousWeight,
  startingWeight,
  goalWeight,
  consistencyScore,
  weekendScore,
  hasAdjustment,
  adjustmentAction,
  calorieChange,
  bucketMessage,
  unit = 'kg',
  className = ''
}) => {
  return (
    <div className={`grid grid-cols-2 gap-3 ${className}`}>
      <WeightChangeTile 
        currentWeight={currentWeight}
        previousWeight={previousWeight}
        unit={unit}
      />
      <TotalProgressTile 
        startingWeight={startingWeight}
        currentWeight={currentWeight}
        goalWeight={goalWeight}
        unit={unit}
      />
      <ConsistencyTile score={consistencyScore} />
      <AdjustmentTile 
        hasAdjustment={hasAdjustment}
        action={adjustmentAction}
        calorieChange={calorieChange}
        bucketMessage={bucketMessage}
      />
    </div>
  );
};

export default {
  BaseTile,
  WeightChangeTile,
  TotalProgressTile,
  ConsistencyTile,
  AdjustmentTile,
  WeekendTile,
  ComfortTile,
  ProgressTilesGrid
};
